import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticles } from "@/hooks/useArticles";
import SectionHeader from "@/components/base/SectionHeader";
import ArticleCard from "@/components/base/ArticleCard";

export default function SuccessStoriesSection() {
  const { t } = useTranslation();
  const { articles, loading } = useArticles({ category: "Success Story", limit: 5 });
  const [featured, ...rest] = articles;

  if (loading) {
    return (
      <section className="py-16 bg-[#fdf8f3]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
            <div className="h-4 bg-gray-100 rounded w-80 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3 bg-gray-200 rounded-2xl h-96"></div>
              <div className="lg:col-span-2 flex flex-col gap-4">
                {[1, 2, 3].map((i) => <div key={i} className="bg-gray-200 rounded-2xl h-28"></div>)}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!featured) return null;

  return (
    <section className="py-16 bg-[#fdf8f3]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionHeader
          title={t("section.successStories.title")}
          subtitle={t("section.successStories.subtitle")}
          viewAllLink="/success-stories"
          accent="orange"
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Featured Large Card */}
          <Link
            to={`/article/${featured.id}`}
            className="lg:col-span-3 group relative rounded-2xl overflow-hidden cursor-pointer block"
            style={{ minHeight: "260px" }}
          >
            <img
              src={featured.image}
              alt={featured.title}
              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            <div className="absolute top-4 left-4">
              <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                {featured.category}
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="flex items-center gap-2 text-yellow-400 text-sm mb-3">
                <i className="ri-map-pin-line"></i>
                <span>{featured.countryFlag} {featured.country}</span>
                <span className="mx-1">·</span>
                <span>{featured.sector}</span>
              </div>
              <h3 className="text-white font-bold text-xl md:text-2xl leading-tight mb-3 line-clamp-2">
                {featured.title}
              </h3>
              <p className="text-white/70 text-sm line-clamp-2 mb-4">{featured.summary}</p>
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-xs flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {featured.entrepreneur}
                </span>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-500 text-white group-hover:bg-orange-400 transition-colors">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          </Link>

          {/* Side Cards */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            {rest.map((article) => (
              <ArticleCard key={article.id} article={article as Parameters<typeof ArticleCard>[0]["article"]} size="sm" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
